/**
 * The designer's tokens, as the single styling source (DESIGN_BRIEF; DQ-24 answered by the v0.1
 * delivery, `docs/03_DESIGN/exports/v0.1/tokens.css`).
 *
 * `src/tokens.css` is a byte-for-byte copy of that export — a test asserts it — and is never edited
 * here: a change to a token is a change to the design delivery, which the designer makes and the
 * copy follows. `src/additions.css` names the values the delivered pages used without naming them
 * (the audit lists each one), so every surface can be linted for hard-coded styles with nothing
 * left over. `generated/tokens.json` is produced from both by `tools/index/gen-design-tokens.mjs`
 * (`pnpm oc:tokens`) so TypeScript can read a token's value without parsing CSS at runtime.
 */
import generated from "../generated/tokens.json" with { type: "json" };
import cssText from "../generated/tokens-css.json" with { type: "json" };
import fontData from "../generated/font-base64.json" with { type: "json" };
export { findHardCoded, isTokenFile, TOKEN_FILES } from "./lint.mjs";

export interface TokenTable {
  _generated: string;
  /** Every `--oc-*` custom property, first declaration wins (the 375px override is listed separately). */
  tokens: Record<string, string>;
  /** The `@media (max-width: 420px)` overrides, by name. */
  small_screen: Record<string, string>;
  /** Class names tokens.css defines (marks, waterfall, banners, states). */
  classes: string[];
}

export const TOKENS: TokenTable = generated as TokenTable;

/** The names, for a test or a lint that wants to know whether `--oc-x` exists. */
export const tokenNames = (): string[] => Object.keys(TOKENS.tokens);

/** `cssVar("ink")` → `var(--oc-ink)`; throws on a token that does not exist, so a typo fails a test rather than rendering black. */
export function cssVar(name: string): string {
  const full = name.startsWith("--oc-") ? name : `--oc-${name}`;
  if (!(full in TOKENS.tokens)) throw new Error(`no design token named ${full}`);
  return `var(${full})`;
}

/** The four figure marks, exactly as tokens.css classes them (Design System §05). */
export const MARK_CLASS = { inferred: "oc-m1", stated: "oc-m2", verified: "oc-m3", indication: "oc-mi" } as const;
export type MarkName = keyof typeof MARK_CLASS;

/** Order and SLA states (Design System §15; tokens.css v0.2). */
export const STATE_CLASS = {
  past: "oc-state-past", current: "oc-state-current", ahead: "oc-state-ahead", held: "oc-state-held", signed: "oc-state-signed",
} as const;
export type StateKind = keyof typeof STATE_CLASS;

/** Palette roles, so a component can say which of the six it means without repeating a hex. */
export const COLOUR = {
  paper: "--oc-paper", ink: "--oc-ink", pencil: "--oc-pencil", rule: "--oc-rule", secondary: "--oc-secondary", verified: "--oc-verified",
} as const;

/** The two stylesheets as text, for a document that must carry its own CSS (the report PDF). */
export const TOKENS_CSS: string = (cssText as { tokens_css: string }).tokens_css;
export const ADDITIONS_CSS: string = (cssText as { additions_css: string }).additions_css;
/** The licensed local variable font, embedded for raw-HTML PDF rendering where relative URLs have no base. */
export const BRICOLAGE_WOFF2_DATA_URI = `data:font/woff2;base64,${(fontData as { woff2_base64: string }).woff2_base64}`;
